<template>
  <!-- Every head tag that changes per page lives here, not in app.blade.php:
       Blade only runs on a full page load, so after an Inertia visit a
       Blade-rendered canonical/description/robots would still describe the
       page the visitor first landed on. Inertia's head manager replaces these
       on every navigation and dedupes them against the SSR output by key. -->
  <InertiaHead :title="documentTitle">
    <link v-if="canonical" data-inertia="canonical" rel="canonical" :href="canonical" />
    <meta v-if="metaDescription" data-inertia="description" name="description" :content="metaDescription" />
    <meta v-if="robots" data-inertia="robots" name="robots" :content="robots" />
  </InertiaHead>

  <template v-for="(section, i) in sections" :key="section.id">
    <!-- Landing target for NtLayout's "Skip to content" link. Sits at the
         boundary between the header chrome and the page's own sections, so
         skipping lands past the nav. tabindex="-1" makes it programmatically
         focusable (it isn't in the tab order), which is what the link needs
         to actually move focus. -->
    <div v-if="i === mainStartIndex" id="main" tabindex="-1"></div>

    <NtSection
      :section="section"
      :index="i"
      :max-index="sections.length - 1"
      :class="[
        (stickyHeader || pinsHeader(section)) && hasHeaderModule(section) ? 'sticky top-0 z-40 transition-transform duration-300' : null,
        stickyHeader && hasHeaderModule(section) && headerHidden && !pinsHeader(section) ? '-translate-y-full' : null,
        isFooterSection(i) ? 'print:hidden' : null
      ]" />
  </template>
</template>

<script>
import { Head as InertiaHead } from '@inertiajs/vue3'
import NtLayout from '../components/layout/NtLayout.vue'
import NtSection from '../components/layout/NtSection.vue'
import { installSearchHistoryCapture } from '@/js/composables/useSearchHistory.js'

const HEADER_MODULE_CLASS = 'App\\Modules\\HeaderModule'

// Don't slide the sticky header away until the visitor is this far down — keeps
// it put through the small scrolls near the top of the page.
const HIDE_AFTER = 120

export default {
  layout: NtLayout,
  components: { InertiaHead, NtSection },
  data() {
    return {
      // Sticky-header hide-on-scroll: true while scrolling down (past
      // HIDE_AFTER), false again as soon as the visitor scrolls back up.
      headerHidden: false,
      lastScrollY: 0
    }
  },
  computed: {
    // Comes from SeoService (`seo.document_title`) rather than being recomposed
    // here, so module-level overrides — a blog post's own title, an area
    // guide's, a property's — carry through, and app.blade.php's `<title>` and
    // this one can never disagree.
    documentTitle() {
      return this.$page.props.seo?.title || this.$page.props.page?.name || this.$page.props.website?.name || ''
    },
    // Same source as the title: SeoService has already applied any module
    // overrides (a blog post's canonical, a property's description), so these
    // never disagree with what the server rendered on a fresh load.
    canonical() {
      return this.$page.props.seo?.canonical || null
    },
    metaDescription() {
      return this.$page.props.seo?.description || null
    },
    robots() {
      return this.$page.props.seo?.robots || null
    },
    // Flatten chrome + page + chrome into one continuous section list so
    // they all render in a single loop. Sections containing a header
    // module get the sticky treatment (see below).
    sections() {
      return [
        ...(this.$page.props.chrome?.header?.sections ?? []),
        ...(this.$page.props.page?.sections ?? []),
        ...(this.$page.props.chrome?.footer?.sections ?? [])
      ]
    },
    // Index of the first non-chrome section, i.e. where the page's own
    // content starts. The skip-link target is rendered just before it.
    mainStartIndex() {
      return this.$page.props.chrome?.header?.sections?.length ?? 0
    },
    // Index of the first chrome-footer section in the flattened list.
    // Anything at or after this index is footer chrome and gets hidden
    // from print output.
    footerStartIndex() {
      const headerLen = this.$page.props.chrome?.header?.sections?.length ?? 0
      const pageLen = this.$page.props.page?.sections?.length ?? 0
      return headerLen + pageLen
    },
    // Page-level override wins (nullable — null means "inherit"); falls
    // back to the website-level setting. Disabled in the admin CMS so the
    // sticky element doesn't shadow the editor's own overlays.
    stickyHeader() {
      if (this.$page.props.isAdmin) {
        return false
      }
      const pageOverride = this.$page.props.page?.sticky_header
      if (pageOverride !== null && pageOverride !== undefined) {
        return !!pageOverride
      }
      return !!this.$page.props.website?.sticky_header
    }
  },
  mounted() {
    // Capture every property-search URL the visitor lands on (including
    // same-tab Inertia navigations) so the breadcrumb on the next property
    // detail page can offer a "back to search" link with the filters
    // preserved. Detached from NtBreadcrumbs itself so the capture keeps
    // working even when a module opts to hide the breadcrumb UI.
    this.removeNavigateListener = installSearchHistoryCapture()

    if (typeof window !== 'undefined' && this.stickyHeader) {
      this.lastScrollY = window.scrollY
      window.addEventListener('scroll', this.onScroll, { passive: true })
    }
  },
  beforeUnmount() {
    this.removeNavigateListener?.()
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.onScroll)
    }
  },
  methods: {
    // Hide the pinned header section on the way down, reveal it on the way up.
    onScroll() {
      const y = window.scrollY
      if (y > this.lastScrollY && y > HIDE_AFTER) {
        this.headerHidden = true
      } else if (y < this.lastScrollY) {
        this.headerHidden = false
      }
      this.lastScrollY = y <= 0 ? 0 : y
    },
    // Atlas 2's header carries the property search bar (the hero teleports it
    // into the header), so the two stay frozen at the top together: always
    // pinned, whatever the site's sticky-header setting, and never slid away
    // on scroll the way the ordinary sticky header is. Off in the admin, where
    // a pinned bar would cover the editing panels.
    pinsHeader(section) {
      if (this.$page.props.isAdmin) {
        return false
      }

      return (section?.modules || []).some((m) => m?.variant?.component === 'NtHeaderAtlas2Variant')
    },
    // True when the section hosts a HeaderModule — used to decide which
    // section gets pinned to the top when `stickyHeader` is on. The
    // server flattens `module_class` onto the variant as a direct
    // attribute (PageController + HandleInertiaRequests) so we don't
    // have to ship the full `variant.module` relation.
    hasHeaderModule(section) {
      return (section.modules ?? []).some((m) => m.variant?.module_class === HEADER_MODULE_CLASS)
    },
    isFooterSection(index) {
      return index >= this.footerStartIndex
    }
  }
}
</script>
